import { Tooltip } from 'bootstrap';

import { InPlaceEditor } from '@syncfusion/ej2-inplace-editor';

import { routes } from '@/shared/routes';
import { SubTask } from '@/shared/types/subTask';
import { formatDate, formatSubTaskCompletedTooltip } from '@/shared/utils/format';
import {
  SubTaskRow,
  defaultDepthForNewItem,
  flattenSubTasks,
  rowsToSubTasks,
  subTasksStructureEqual,
} from '@/shared/utils/subTask';

import { POST } from '@/front/fetch';
import { confirmModal } from '@/front/modal';
import { initTableRowReorder } from '@/front/reorder';

const getTableRows = (table: HTMLTableElement): HTMLTableRowElement[] =>
  [...table.querySelectorAll('tr.sub-task')] as HTMLTableRowElement[];

const getRowDepth = (tr: HTMLTableRowElement): 0 | 1 => (parseInt(tr.dataset.depth ?? '0', 10) || 0) as 0 | 1;

const getPhaseChildRows = (tr: HTMLTableRowElement, rows: HTMLTableRowElement[]): HTMLTableRowElement[] => {
  const index = rows.indexOf(tr);

  if (index < 0 || getRowDepth(tr) !== 0) {
    return [];
  }

  const children: HTMLTableRowElement[] = [];

  for (let i = index + 1; i < rows.length; i++) {
    if (getRowDepth(rows[i]) === 1) {
      children.push(rows[i]);
    } else {
      break;
    }
  }

  return children;
};

const getTitleFromRow = (tr: HTMLTableRowElement): string | null => {
  const titleInput = tr.querySelector('input[name="subTaskTitles[]"]') as HTMLInputElement | null;

  if (titleInput) {
    const value = titleInput.value.trim();
    return value || null;
  }

  const titleDiv = tr.querySelector('div[data-name="title"]') as HTMLDivElement | null;

  if (!titleDiv) {
    return null;
  }

  const fromDataset = titleDiv.dataset.value?.trim();

  if (fromDataset) {
    return fromDataset;
  }

  const inPlaceEditorInput = titleDiv.querySelector('input') as HTMLInputElement | null;
  const fromEditor = inPlaceEditorInput?.value.trim();

  return fromEditor || null;
};

const getFlatRowsFromTable = (table: HTMLTableElement): SubTaskRow[] =>
  getTableRows(table).map((tr) => {
    const completedCheckBox = tr.querySelector('input[type="checkbox"]') as HTMLInputElement | null;

    return {
      task: getTitleFromRow(tr),
      depth: getRowDepth(tr),
      ...(completedCheckBox
        ? {
            completed: completedCheckBox.checked,
            ...(completedCheckBox.checked
              ? {
                  completedDate: completedCheckBox.dataset.completedDate,
                  completedByUserId: completedCheckBox.dataset.completedByUserId,
                  completedByUserName: completedCheckBox.dataset.completedByUserName,
                }
              : {}),
          }
        : {}),
    };
  });

const clearCompletedDateTooltip = (completedDateTd: HTMLTableCellElement) => {
  Tooltip.getInstance(completedDateTd)?.dispose();
  completedDateTd.removeAttribute('title');
  completedDateTd.removeAttribute('data-bs-toggle');
};

const setCompletedDateTooltip = (completedDateTd: HTMLTableCellElement, date: Date | string, userName?: string) => {
  const tooltip = formatSubTaskCompletedTooltip(date, userName);

  completedDateTd.setAttribute('title', tooltip);
  completedDateTd.setAttribute('data-bs-toggle', 'tooltip');
  Tooltip.getOrCreateInstance(completedDateTd);
};

const normalizeSubTaskRows = (table: HTMLTableElement) => {
  const normalized = flattenSubTasks(rowsToSubTasks(getFlatRowsFromTable(table)));

  getTableRows(table).forEach((tr, index) => {
    const depth = normalized[index]?.depth ?? 0;

    setPendingDepth(tr, depth);

    const depthInput = tr.querySelector('input[name="subTaskDepths[]"]') as HTMLInputElement | null;
    if (depthInput) {
      depthInput.value = String(depth);
    }
  });
};

const refreshRowStates = (table: HTMLTableElement) => {
  const rows = getTableRows(table);

  rows.forEach((tr, index) => {
    const depth = getRowDepth(tr);
    const nextDepth = index + 1 < rows.length ? getRowDepth(rows[index + 1]) : 0;
    const isPhase = depth === 0 && nextDepth === 1;

    tr.classList.toggle('sub-task-child', depth === 1);
    tr.classList.toggle('sub-task-phase', isPhase);

    if (isPhase) {
      tr.classList.remove('completed');

      const completedDateTd = tr.querySelector('td.completed-date') as HTMLTableCellElement | null;
      if (completedDateTd) {
        completedDateTd.innerHTML = '';
      }

      const completedCheckBox = tr.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
      if (completedCheckBox) {
        completedCheckBox.checked = false;
        delete completedCheckBox.dataset.completedDate;
        delete completedCheckBox.dataset.completedByUserId;
        delete completedCheckBox.dataset.completedByUserName;
      }
    }
  });

  syncCheckboxes(table);
};

const syncCheckboxes = (table: HTMLTableElement) => {
  if (table.dataset.ajax !== 'true' || table.dataset.recurring === 'true') return;

  getTableRows(table).forEach((tr) => {
    const th = tr.querySelector('th') as HTMLTableCellElement | null;
    if (!th) return;

    const checkbox = th.querySelector('input[type="checkbox"]');

    if (tr.classList.contains('sub-task-phase')) {
      checkbox?.remove();
      return;
    }

    if (checkbox) return;

    const newCheckbox = document.createElement('input');
    newCheckbox.type = 'checkbox';
    newCheckbox.className = 'form-check-input';
    th.appendChild(newCheckbox);

    initCheckbox(tr, newCheckbox, table);
  });
};

const initCheckbox = (tr: HTMLTableRowElement, checkbox: HTMLInputElement, table: HTMLTableElement) => {
  const completedDateTd = tr.querySelector('td.completed-date') as HTMLTableCellElement | null;

  checkbox.addEventListener('change', async () => {
    const completed = checkbox.checked;
    const completedDate = new Date();

    if (completed) {
      delete checkbox.dataset.completedByUserId;
      delete checkbox.dataset.completedByUserName;
      checkbox.dataset.completedDate = completedDate.toISOString();

      const completedByUserName = table.dataset.currentUserName || undefined;

      if (completedByUserName) {
        checkbox.dataset.completedByUserName = completedByUserName;
      }

      if (completedDateTd) {
        completedDateTd.innerHTML = formatDate(completedDate);
        setCompletedDateTooltip(completedDateTd, completedDate, completedByUserName);
      }
    } else {
      delete checkbox.dataset.completedDate;
      delete checkbox.dataset.completedByUserId;
      delete checkbox.dataset.completedByUserName;

      if (completedDateTd) {
        completedDateTd.innerHTML = '';
        clearCompletedDateTooltip(completedDateTd);
      }
    }

    tr.classList.toggle('completed', completed);

    await update(table);
  });
};

const update = async (table: HTMLTableElement) => {
  updateChecklistDeleteAllVisibility(table);
  updatePersistSubTasksVisibility(table);

  const form = table.closest('form');

  if (form) {
    if (table.dataset.ajax === 'true') {
      const taskId = table.dataset.taskId;
      const recurring = table.dataset.recurring === 'true';
      const url = recurring ? routes.recurringTasks.subTasks({ id: taskId }) : routes.tasks.subTasks({ id: taskId });

      await POST(url, rowsToSubTasks(getFlatRowsFromTable(table)));
    } else {
      form.dataset.dirty = 'true';
    }
  }
};

const getSubTaskRowsFromTable = (table: HTMLTableElement) =>
  getTableRows(table).map((tr) => ({
    task: getTitleFromRow(tr),
    depth: getRowDepth(tr),
  }));

export const updatePersistSubTasksVisibility = (table: HTMLTableElement) => {
  if (table.dataset.ajax !== 'true' || table.dataset.recurring === 'true') {
    return;
  }

  const card = table.closest('.card');
  const toggle = card?.querySelector('[data-role="persist-sub-tasks-toggle"]') as HTMLDivElement | null;
  const templateJson = card?.getAttribute('data-template-sub-tasks');

  if (!toggle || !templateJson) {
    return;
  }

  let templateSubTasks: SubTask[];

  try {
    templateSubTasks = JSON.parse(templateJson) as SubTask[];
  } catch {
    return;
  }

  const currentSubTasks = rowsToSubTasks(getSubTaskRowsFromTable(table));
  const differs = !subTasksStructureEqual(currentSubTasks, templateSubTasks);

  toggle.classList.toggle('d-none', !differs);
  toggle.classList.toggle('d-flex', differs);
};

const setPendingDepth = (tr: HTMLTableRowElement, depth: 0 | 1) => {
  tr.dataset.depth = String(depth);
  tr.classList.toggle('indent-1', depth === 1);
};

const changeRowDepth = async (tr: HTMLTableRowElement, newDepth: 0 | 1) => {
  const table = tr.closest('table') as HTMLTableElement;

  setPendingDepth(tr, newDepth);

  const depthInput = tr.querySelector('input[name="subTaskDepths[]"]') as HTMLInputElement | null;
  if (depthInput) {
    depthInput.value = String(newDepth);
  }

  refreshRowStates(table);
  await update(table);
};

const tryIndentRow = async (tr: HTMLTableRowElement) => {
  const table = tr.closest('table') as HTMLTableElement;
  const rows = getTableRows(table);

  if (getRowDepth(tr) === 0 && rows.indexOf(tr) > 0) {
    await changeRowDepth(tr, 1);
  }
};

const tryUnindentRow = async (tr: HTMLTableRowElement) => {
  if (getRowDepth(tr) === 1) {
    await changeRowDepth(tr, 0);
  }
};

document.addEventListener('DOMContentLoaded', () => {
  [...document.querySelectorAll('table.sub-tasks tr')]
    .map((tr) => tr as HTMLTableRowElement)
    .forEach((tr) => {
      if (tr.classList.contains('sub-task')) {
        initSubTaskRow(tr);
      } else if (tr.classList.contains('sub-task-new')) {
        const table = tr.closest('table') as HTMLTableElement;
        const recurring = table.dataset.recurring === 'true';
        const ajax = table.dataset.ajax === 'true';

        const titleTextBox = tr.querySelector('input[type="text"]') as HTMLInputElement;
        const addButton = tr.querySelector('a.add') as HTMLAnchorElement;
        const completedCheckBox = tr.querySelector('input[type="checkbox"]') as HTMLInputElement | null;

        if (table.dataset.locked) {
          if (completedCheckBox) {
            initCheckbox(tr, completedCheckBox, table);
          }
          return;
        }

        const syncPendingDepth = () =>
          setPendingDepth(tr, defaultDepthForNewItem(getTableRows(table).map((r) => ({ depth: getRowDepth(r) }))));

        syncPendingDepth();

        const addSubTask = async () => {
          const title = titleTextBox.value.trim();

          if (title) {
            const pendingDepth = getRowDepth(tr);
            titleTextBox.value = '';

            let newTr = document.createElement('tr');
            tr.parentNode!.insertBefore(newTr, tr);
            newTr.outerHTML = subTaskRowTemplate({ title, depth: pendingDepth, recurring, ajax });
            newTr = tr.previousElementSibling! as HTMLTableRowElement;
            initSubTaskRow(newTr);

            syncPendingDepth();
            refreshRowStates(table);
            await update(table);
          }
        };

        titleTextBox.addEventListener('keydown', async (e) => {
          if (e.key === 'Tab') {
            e.preventDefault();

            if (e.shiftKey) {
              if (getRowDepth(tr) === 1) setPendingDepth(tr, 0);
            } else {
              if (getRowDepth(tr) === 0 && getTableRows(table).length > 0) setPendingDepth(tr, 1);
            }

            return;
          }

          if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            await addSubTask();
          }
        });

        titleTextBox.addEventListener('input', () => {
          addButton.classList.toggle('active', !!titleTextBox.value.trim());
        });

        addButton.addEventListener('click', async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await addSubTask();
        });
      }
    });

  [...document.querySelectorAll('table.sub-tasks')].forEach((table) => {
    updatePersistSubTasksVisibility(table as HTMLTableElement);
  });
});

export const hasSubTaskItems = (table: HTMLTableElement) => {
  return getTableRows(table).some((tr) => !!getTitleFromRow(tr));
};

export const updateChecklistDeleteAllVisibility = (table: HTMLTableElement) => {
  const card = table.closest('.card');
  const button = card?.querySelector('a[data-role="checklist-delete-all"]') as HTMLAnchorElement | null;

  if (!button) {
    return;
  }

  const hasItems = hasSubTaskItems(table);

  if (hasItems) {
    delete button.dataset.disabled;
    button.removeAttribute('aria-disabled');
  } else {
    button.dataset.disabled = 'true';
    button.setAttribute('aria-disabled', 'true');
  }
};

export const replaceSubTasks = async (table: HTMLTableElement, items: string[] | { task: string; depth: 0 | 1 }[]) => {
  const recurring = table.dataset.recurring === 'true';
  const ajax = table.dataset.ajax === 'true';
  const tbody = table.querySelector('tbody');
  const newRow = tbody?.querySelector('tr.sub-task-new') as HTMLTableRowElement | null;

  if (!tbody || !newRow) {
    return;
  }

  tbody.querySelectorAll('tr.sub-task').forEach((row) => row.remove());

  const normalized = (items as (string | { task: string; depth: 0 | 1 })[])
    .map((item) => (typeof item === 'string' ? { task: item, depth: 0 as const } : item))
    .filter((item) => item.task.trim().length > 0);

  normalized.forEach(({ task: title, depth }) => {
    let newSubTaskRow = document.createElement('tr');
    newRow.parentNode!.insertBefore(newSubTaskRow, newRow);
    newSubTaskRow.outerHTML = subTaskRowTemplate({ title, depth, recurring, ajax });
    newSubTaskRow = newRow.previousElementSibling as HTMLTableRowElement;
    initSubTaskRow(newSubTaskRow);
  });

  const newTitleInput = newRow.querySelector('input[type="text"]') as HTMLInputElement | null;
  if (newTitleInput) {
    newTitleInput.value = '';
  }

  refreshRowStates(table);
  await update(table);
};

const initSubTaskRow = (tr: HTMLTableRowElement) => {
  const table = tr.closest('table') as HTMLTableElement;

  const titleInput = tr.querySelector('input[name="subTaskTitles[]"]') as HTMLInputElement | null;
  const titleDiv = tr.querySelector('div[data-name="title"]') as HTMLDivElement | null;
  const completedCheckBox = tr.querySelector('input[type="checkbox"]') as HTMLInputElement | null;
  const deleteButton = tr.querySelector('a.delete') as HTMLAnchorElement | null;

  if (table.dataset.locked) {
    if (completedCheckBox) {
      initCheckbox(tr, completedCheckBox, table);
    }
    return;
  }

  if (titleInput) {
    titleInput.addEventListener('keyup', async () => {
      await update(table);
    });

    titleInput.addEventListener('keydown', async (e) => {
      if (e.key !== 'Tab') return;
      e.preventDefault();
      if (e.shiftKey) await tryUnindentRow(tr);
      else await tryIndentRow(tr);
    });
  } else if (titleDiv) {
    titleDiv.setAttribute('data-underline', 'false');

    const titleEditor = new InPlaceEditor({
      mode: 'Inline',
      name: titleDiv.dataset.name,
      value: titleDiv.dataset.value,
      actionOnBlur: 'Cancel',
      model: { autocomplete: 'off', showClearButton: false },
      validationRules: { title: { required: true } },
      submitClick: async () => {
        titleDiv.dataset.value = titleEditor.value.toString();
        await update(table);
      },
    });

    titleEditor.appendTo(titleDiv);

    titleDiv.addEventListener(
      'keydown',
      async (e) => {
        if (e.key !== 'Tab') return;

        const innerInput = titleDiv.querySelector('input') as HTMLInputElement | null;
        if (!innerInput) return;

        e.preventDefault();
        e.stopPropagation();

        const currentValue = innerInput.value.trim();
        if (currentValue) titleDiv.dataset.value = currentValue;

        if (e.shiftKey) await tryUnindentRow(tr);
        else await tryIndentRow(tr);
      },
      true,
    );
  }

  if (completedCheckBox) {
    initCheckbox(tr, completedCheckBox, table);
  }

  if (deleteButton) {
    deleteButton.addEventListener('click', async (e) => {
      e.preventDefault();

      const rows = getTableRows(table);
      const childRows = getPhaseChildRows(tr, rows);

      if (childRows.length > 0) {
        const message =
          childRows.length === 1
            ? 'Deleting this phase will also delete the sub-item inside. Do you want to continue?'
            : `Deleting this phase will also delete all ${childRows.length} sub-items inside. Do you want to continue?`;

        if (!(await confirmModal(message, 'Delete', 'danger'))) {
          return;
        }

        childRows.forEach((child) => child.remove());
      }

      tr.remove();
      refreshRowStates(table);
      await update(table);
    });
  }

  initTableRowReorder(tr, {
    onReorder: async () => {
      normalizeSubTaskRows(table);
      refreshRowStates(table);
      await update(table);
    },
    onIndent: async () => tryIndentRow(tr),
    onUnindent: async () => tryUnindentRow(tr),
  });
};

const subTaskRowTemplate = (params: { title: string; depth?: 0 | 1; recurring?: boolean; ajax?: boolean }) => {
  const { title, depth = 0, recurring, ajax } = params;
  const depthClass = depth === 1 ? ' sub-task-child' : '';
  const escapedTitle = title.replaceAll('"', '&quot;');

  const checkboxCol = !recurring
    ? ajax
      ? '<th><input type="checkbox" class="form-check-input" /></th>'
      : '<th><input name="subTaskCompleteds[]" type="checkbox" class="form-check-input" /></th>'
    : '';

  const titleCol = ajax
    ? `<td class="w-100"><div data-name="title" data-value="${escapedTitle}" data-fullwidth="true"></div></td>`
    : `<td class="w-100"><input name="subTaskTitles[]" type="text" value="${escapedTitle}" /><input type="hidden" name="subTaskDepths[]" value="${depth}" /></td>`;

  const completedDateCol = !recurring ? '<td class="completed-date"></td>' : '';

  return `<tr class="sub-task${depthClass}" draggable="true" data-depth="${depth}">
    <td><span class="reorder"><i class="fa-solid fa-grip-dots-vertical"></i></span></td>
    ${checkboxCol}
    ${titleCol}
    ${completedDateCol}
    <td><a href="#" class="delete"><i class="fa-solid fa-trash"></i></a></td>
  </tr>`;
};
